import {DateTime} from 'luxon';

import {
  AggregationMethod,
  ApiForecast,
  ForecastTimeSeriesQuery,
  TimeAggregation,
  WeatherColumns,
} from 'src/lib/api/series';

import {DynamicalUnavailableError} from './errors';
import {convertValue, displayUnit, isVolumeMode} from './units';

/**
 * Client-side port of the server's forecast response shaping
 * (make_forecast_query_response + resample_ffill + convert_units in
 * backend/api/blueprints/public_api/v2/hydroforecast/time_series.py and
 * backend/models/hydrology_common/resampling.py), restricted to what weather
 * queries exercise: values are start-of-period, upsampled hourly with forward
 * fill, bucketed by wall-clock time in the site's time zone, aggregated with
 * mean/min/max, then unit-converted (with rate→volume integration over each
 * step when a volume mode is requested).
 */

/** Matches the server's _ROUND_PRECISION so parity checks compare like for like. */
const ROUND_PRECISION = 5;
const HOUR_MS = 3600 * 1000;

export type NativeForecast = {
  initTimeMs: number;
  /** Lead hours ascending, already mapped to the API's lead grid (planLeads). */
  leadTimeHours: number[];
  /** Per requested column: temperature in °C, precipitation as mm/h rate. */
  values: {[column in WeatherColumns]?: Float64Array};
};

type Series = {timesMs: number[]; values: {[column: string]: number[]}};

function isAllNaNRow(values: Series['values'], columns: string[], index: number): boolean {
  return columns.every((column) => Number.isNaN(values[column][index]));
}

/** Drop rows where every column is NaN (pandas .dropna(how="all")). */
function dropAllNaNRows(series: Series, columns: string[]): Series {
  const keep: number[] = [];
  for (let i = 0; i < series.timesMs.length; i += 1) {
    if (!isAllNaNRow(series.values, columns, i)) {
      keep.push(i);
    }
  }
  return {
    timesMs: keep.map((i) => series.timesMs[i]),
    values: Object.fromEntries(
      columns.map((column) => [column, keep.map((i) => series.values[column][i])])
    ),
  };
}

function bucketStartMs(
  timeMs: number,
  aggregation: TimeAggregation,
  zone: string,
  originMs: number
): number {
  const dt = DateTime.fromMillis(timeMs, {zone});
  switch (aggregation) {
    case '1H':
      return dt.startOf('hour').toMillis();
    case '6H': {
      // pandas '6H' is a fixed frequency: real 6-hour intervals anchored at the
      // local start-of-day of the series' first timestamp (origin="start_day"),
      // NOT wall-clock hour blocks. On DST-transition days the two disagree —
      // after spring-forward the pandas bins land on 01:00/07:00/… local.
      const SIX_HOURS_MS = 6 * HOUR_MS;
      return originMs + Math.floor((timeMs - originMs) / SIX_HOURS_MS) * SIX_HOURS_MS;
    }
    case '1D':
      // pandas Day frequency respects the calendar: buckets at local midnight,
      // 23/25-hour buckets across DST transitions.
      return dt.startOf('day').toMillis();
    default:
      // The direct path declining an aggregation it doesn't implement (10D, MS, …)
      // is an expected fallback to the API, not a defect.
      throw new DynamicalUnavailableError(`Unsupported time aggregation for direct reads: ${aggregation}`);
  }
}

function aggregate(bucket: number[], method: AggregationMethod): number {
  let count = 0;
  let acc = method === 'min' ? Infinity : method === 'max' ? -Infinity : 0;
  for (const value of bucket) {
    if (Number.isNaN(value)) {
      continue;
    }
    count += 1;
    if (method === 'min') {
      acc = Math.min(acc, value);
    } else if (method === 'max') {
      acc = Math.max(acc, value);
    } else {
      acc += value;
    }
  }
  if (count === 0) {
    return NaN;
  }
  return method === 'mean' ? acc / count : acc;
}

/**
 * Hourly upsample with forward fill over [startMs, endMs), then aggregate into
 * wall-clock buckets. Mirrors resample_ffill: the upsample grid is fixed hourly
 * steps (DST-safe), while bucket boundaries follow the local wall clock.
 */
function resampleSeries(
  series: Series,
  columns: string[],
  aggregation: TimeAggregation,
  method: AggregationMethod,
  startMs: number,
  endMs: number,
  zone: string
): Series {
  const bucketTimes: number[] = [];
  const buckets = new Map<number, {[column: string]: number[]}>();
  const originMs = DateTime.fromMillis(startMs, {zone}).startOf('day').toMillis();
  let sourceIndex = -1;
  for (let t = startMs; t < endMs; t += HOUR_MS) {
    while (sourceIndex + 1 < series.timesMs.length && series.timesMs[sourceIndex + 1] <= t) {
      sourceIndex += 1;
    }
    const bucket = bucketStartMs(t, aggregation, zone, originMs);
    let bucketValues = buckets.get(bucket);
    if (!bucketValues) {
      bucketValues = Object.fromEntries(columns.map((column) => [column, []]));
      buckets.set(bucket, bucketValues);
      bucketTimes.push(bucket);
    }
    for (const column of columns) {
      bucketValues[column].push(
        sourceIndex >= 0 ? series.values[column][sourceIndex] : NaN
      );
    }
  }
  return {
    timesMs: bucketTimes,
    values: Object.fromEntries(
      columns.map((column) => [
        column,
        bucketTimes.map((bucket) => aggregate(buckets.get(bucket)![column], method)),
      ])
    ),
  };
}

/**
 * Rate → per-step volume (and optional accumulation), the convert_units volume
 * branch: each step's depth is its mm/h rate times the step length in hours, with
 * the final step extending to the end of the valid period.
 */
function ratesToVolumes(
  values: number[],
  timesMs: number[],
  validEndMs: number,
  accumulate: boolean
): number[] {
  const out = new Array<number>(values.length);
  let runningTotal = 0;
  for (let i = 0; i < values.length; i += 1) {
    const stepEndMs = i + 1 < timesMs.length ? timesMs[i + 1] : validEndMs;
    const depth = values[i] * ((stepEndMs - timesMs[i]) / HOUR_MS);
    if (accumulate) {
      // numpy cumsum semantics: NaN poisons the running total from there on.
      runningTotal += depth;
      out[i] = runningTotal;
    } else {
      out[i] = depth;
    }
  }
  return out;
}

/** Offset cumulative values so the last step at or before `nowMs` reads 0. */
function offsetCumulativeFromNow(values: number[], timesMs: number[], nowMs: number): number[] {
  let offsetIndex = -1;
  for (let i = 0; i < timesMs.length && timesMs[i] <= nowMs; i += 1) {
    offsetIndex = i;
  }
  if (offsetIndex < 0) {
    return values;
  }
  const offset = values[offsetIndex];
  if (Number.isNaN(offset)) {
    return values;
  }
  return values.map((value, i) => (timesMs[i] < nowMs ? 0 : value - offset));
}

function roundForApi(value: number): number {
  // The API rounds with numpy before serializing; nulls are produced by the
  // caller when converting NaN.
  return Math.round(value * 10 ** ROUND_PRECISION) / 10 ** ROUND_PRECISION;
}

export function shapeForecast(
  native: NativeForecast,
  query: ForecastTimeSeriesQuery,
  zone: string,
  nowMs: number = Date.now()
): ApiForecast {
  const columns = Object.keys(native.values) as WeatherColumns[];
  let series: Series = {
    timesMs: native.leadTimeHours.map((lead) => native.initTimeMs + lead * HOUR_MS),
    values: Object.fromEntries(
      columns.map((column) => [column, Array.from(native.values[column]!)])
    ),
  };

  // Valid period: first/last rows with any data; the (exclusive) end extends one
  // step past the last, inferred from the second-to-last step size.
  const validRows = series.timesMs.filter((_, i) => !isAllNaNRow(series.values, columns, i));
  const validStartMs = validRows[0] ?? NaN;
  const validEndMs =
    validRows.length >= 2
      ? validRows[validRows.length - 1] +
        (validRows[validRows.length - 1] - validRows[validRows.length - 2])
      : validRows.length === 1
        ? validRows[0] + HOUR_MS
        : NaN;

  if (query.timeAggregation && validRows.length > 0) {
    series = resampleSeries(
      series,
      columns,
      query.timeAggregation,
      query.aggregationMethod ?? 'mean',
      validStartMs,
      validEndMs,
      zone
    );
    // clamp_index_to_start_date: the first bucket's label can precede the data it
    // aggregates (e.g. a daily bucket for data starting mid-day).
    if (series.timesMs.length > 0 && series.timesMs[0] < validStartMs) {
      series.timesMs[0] = validStartMs;
    }
  }

  series = dropAllNaNRows(series, columns);

  const volumeMode = isVolumeMode(query.rateVolumeMode);
  const data: {[column: string]: Array<number | null>} = {};
  for (const column of columns) {
    let values = series.values[column];
    if (column === WeatherColumns.PRECIPITATION && volumeMode) {
      const accumulate =
        query.rateVolumeMode === 'cumulative-volume' ||
        query.rateVolumeMode === 'cumulative-volume-from-now';
      values = ratesToVolumes(values, series.timesMs, validEndMs, accumulate);
      if (query.rateVolumeMode === 'cumulative-volume-from-now') {
        values = offsetCumulativeFromNow(values, series.timesMs, nowMs);
      }
    }
    data[column] = values.map((value) => {
      const converted = convertValue(value, column, query.unitSystem);
      return Number.isFinite(converted) ? roundForApi(converted) : null;
    });
  }

  return {
    initializationTime: new Date(native.initTimeMs),
    issueTime: new Date(native.initTimeMs),
    validTimes: series.timesMs.map((t) => new Date(t)),
    validTimeRange: [new Date(validStartMs), new Date(validEndMs)],
    // The API serializes NaN as null; ApiForecast types data as number[] and every
    // consumer already tolerates the nulls that come back from JSON.
    data: data as ApiForecast['data'],
  };
}

export function variablesForQuery(
  query: ForecastTimeSeriesQuery,
  columns: WeatherColumns[]
): {[column: string]: {unit: string}} {
  return Object.fromEntries(
    columns.map((column) => [
      column,
      {unit: displayUnit(column, query.unitSystem, query.rateVolumeMode)},
    ])
  );
}
